import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import './RowingArcPrototype.css';
import { createUnifiedLegend, positionLegend, type LegendItem } from '../../utils/d3LegendSystem';
import { useArcMetrics, type StrokeSample as ArcStrokeSample } from '../../hooks/useArcMetrics';
import { StrokeSample as ArcStrokeData } from './rowingArcTypes';
import {
  alphaToTheta,
  formatAngle,
  buildBladePath,
  calculateStrokeGeometry,
  type SegmentGeometry,
  type StrokeGeometry,
} from '../../utils/arcGeometry';
import { ARC_DISPLAY, ARC_GEOMETRY, ARC_ANIMATION, ARC_PHYSICS } from './arcConfig';
import {
  getArcChartChrome,
  getArcSegmentPaintById,
  isChartDarkTheme,
} from '../../theme/chartChrome';
import { useTheme } from '../../contexts/ThemeContext';

function applyArcThemeColors(svg: SVGSVGElement | null) {
  if (!svg) return;
  const arc = getArcChartChrome();
  const paint = arc.segments;
  const root = d3.select(svg);
  root.selectAll('.arc-degree-tick').attr('stroke', arc.axisTick);
  root.selectAll('.arc-degree-label').attr('fill', arc.axisLabel);
  root.selectAll('.arc-bow-label, .arc-stern-label').attr('fill', arc.axisLabel);
  root.selectAll('.arc-boat-centerline').attr('stroke', arc.axisTick);
  root.selectAll('.arc-full-circumference').attr('stroke', arc.grid);
  root.selectAll('.arc-reference-line').attr('stroke', arc.referenceLine);
  root
    .selectAll('.total-arc-outline')
    .attr('stroke', arc.totalArc)
    .attr('opacity', arc.totalArcOpacity);
  root.selectAll('.arc-oar-shaft').attr('stroke', arc.oarShaft);
  root.selectAll('.arc-oar-pivot').attr('fill', arc.oarPivot);
  root.selectAll('.arc-oar-blade').attr('fill', arc.oarShaft).attr('stroke', arc.oarShaft);
  root
    .selectAll('.arc-segment.catch-slip')
    .attr('fill', paint.catchSlip.fill)
    .attr('stroke', paint.catchSlip.stroke);
  root
    .selectAll('.arc-segment.effective-length')
    .attr('fill', paint.effectiveLength.fill)
    .attr('stroke', paint.effectiveLength.stroke);
  root
    .selectAll('.arc-segment.finish-slip')
    .attr('fill', paint.finishSlip.fill)
    .attr('stroke', paint.finishSlip.stroke);
  root.selectAll('.legend-label').attr('fill', arc.axisLabel).style('opacity', 1);

  root.selectAll<SVGGElement, LegendItem>('.unified-legend .legend-item').each(function (d) {
    const segmentPaint = getArcSegmentPaintById(d.key);
    if (!segmentPaint) return;
    const circle = d3.select(this).select('circle');
    if (isChartDarkTheme()) {
      circle
        .attr('fill', segmentPaint.stroke)
        .attr('stroke', segmentPaint.stroke)
        .attr('stroke-width', 2);
    } else {
      circle.attr('fill', '#FFFFFF').attr('stroke-width', 2);
    }
  });
}

function styleLegendFocus(
  legendItems: d3.Selection<SVGGElement, LegendItem, SVGGElement, unknown>,
  focusId: string | null
) {
  const arc = getArcChartChrome();
  legendItems
    .classed('legend-item--active', (d) => !!focusId && focusId === d.key)
    .each(function (d) {
      const inactive = !!focusId && focusId !== d.key;
      const item = d3.select(this);
      item.style('opacity', null);
      item.select('circle').style('opacity', inactive ? arc.legendInactiveOpacity : 1);
      item
        .select('text.legend-label')
        .attr('fill', inactive ? arc.axisTick : arc.axisLabel)
        .style('opacity', 1);
    });
}

// Vertical space reserved below the arc so the legend does not crowd the diagram.
const ARC_LEGEND_RESERVE = 52;
const defaultDisplayConfig = ARC_DISPLAY;
type DisplayConfig = typeof defaultDisplayConfig;
type SegmentDatum = SegmentGeometry & { id: string; fill: string; stroke: string };

const buildSegmentData = (geometry: StrokeGeometry): SegmentDatum[] => {
  const segments = [
    { id: 'catch-slip', ...geometry.segments.catchSlip },
    { id: 'effective-length', ...geometry.segments.effectiveLength },
    { id: 'finish-slip', ...geometry.segments.finishSlip },
  ];

  return segments.map((segment) => {
    const paint = getArcSegmentPaintById(segment.id);
    return {
      ...segment,
      fill: paint?.fill ?? segment.color,
      stroke: paint?.stroke ?? '#FFFFFF',
    };
  });
};

const createTooltip = () => {
  const arc = getArcChartChrome();
  return d3
    .select('body')
    .selectAll<HTMLDivElement, unknown>('.rowing-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'rowing-tooltip')
    .style('position', 'absolute')
    .style('padding', '8px 12px')
    .style('background', arc.tooltipBg)
    .style('color', arc.tooltipFg)
    .style('border-radius', '4px')
    .style('font-size', '12px')
    .style('font-family', 'Inter, sans-serif')
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', 1000);
};

const formatSegmentRange = (startAlpha: number, endAlpha: number): string =>
  `${formatAngle(startAlpha)} → ${formatAngle(endAlpha)}`;

const renderSegments = (
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  segmentData: SegmentDatum[],
  arcGenerator: d3.Arc<any, SegmentDatum>,
  tooltip: d3.Selection<HTMLDivElement, unknown, d3.BaseType, unknown>,
  animationConfig: typeof defaultAnimationConfig,
  handleSegmentToggle: (segmentId: string) => void,
  activeSegmentRef: React.MutableRefObject<string | null>,
  isInitialRender: boolean = false,
  animateGeometryUpdates: boolean = false
) => {
  const arcChrome = getArcChartChrome();
  const resolveActive = () => activeSegmentRef.current;
  const isActive = (id: string) => {
    const current = resolveActive();
    return current ? current === id : false;
  };
  const segmentOpacity = (id: string) =>
    resolveActive() ? (isActive(id) ? 1 : arcChrome.inactiveSegmentOpacity) : 1;
  const segmentStrokeWidth = (id: string) =>
    isActive(id) ? arcChrome.segmentActiveStrokeWidth : arcChrome.segmentStrokeWidth;

  const selection = group
    .selectAll<SVGPathElement, SegmentDatum>('.arc-segment')
    .data(segmentData, (d) => d.id);

  const enterSelection = selection
    .enter()
    .append('path')
    .attr('class', (d) => `arc-segment ${d.id}`)
    .attr('stroke', (d) => d.stroke)
    .attr('tabindex', 0)
    .attr('role', 'button')
    .attr('aria-label', (d) => `${d.name}: ${Math.abs(d.endAlpha - d.startAlpha).toFixed(1)}°`)
    .on('mouseover', function (event, d) {
      tooltip
        .html(
          `<strong>${d.name}</strong><br/>${formatSegmentRange(
            d.startAlpha,
            d.endAlpha
          )}<br/>Arc Length: ${Math.abs(d.endAlpha - d.startAlpha).toFixed(1)}°`
        )
        .style('left', `${event.pageX + 12}px`)
        .style('top', `${event.pageY - 20}px`)
        .transition()
        .duration(200)
        .style('opacity', 1);

      d3.select(this)
        .transition()
        .duration(150)
        .attr('stroke-width', 3.2)
        .style('filter', 'brightness(1.1)');
    })
    .on('mouseout', function (_event, d) {
      tooltip.transition().duration(150).style('opacity', 0);
      const currentActive = resolveActive();
      d3.select(this)
        .transition()
        .duration(150)
        .attr('stroke-width', currentActive && currentActive === d.id ? 3 : 2)
        .style('filter', 'none');
    })
    .on('click', (_, d) => handleSegmentToggle(d.id))
    .on('keydown', (event, d) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSegmentToggle(d.id);
      }
    });

  const mergedSelection = enterSelection.merge(selection);

  // Update all segments (both new and existing) with current geometry
  mergedSelection.interrupt();

  if (animateGeometryUpdates) {
    mergedSelection
      .transition()
      .duration(220)
      .ease(d3.easeCubicOut)
      .attr('d', (d) => arcGenerator(d) || undefined)
      .attr('fill', (d) => d.fill)
      .attr('stroke', (d) => d.stroke)
      .attr('stroke-width', (d) => segmentStrokeWidth(d.id))
      .attr('opacity', (d) => segmentOpacity(d.id))
      .attr('aria-pressed', (d) => isActive(d.id));
  } else {
    mergedSelection
      .attr('d', (d) => arcGenerator(d) || undefined)
      .attr('fill', (d) => d.fill)
      .attr('stroke', (d) => d.stroke)
      .attr('stroke-width', (d) => segmentStrokeWidth(d.id))
      .attr('opacity', (d) => segmentOpacity(d.id))
      .attr('aria-pressed', (d) => isActive(d.id));
  }

  // Only animate entrance on initial render, not on geometry updates
  if (animationConfig.enabled && isInitialRender && enterSelection.size() > 0) {
    enterSelection
      .attr('opacity', 0)
      .transition()
      .duration(animationConfig.duration)
      .delay((_, i) => i * animationConfig.delay)
      .attr('opacity', (d) => segmentOpacity(d.id));
  }

  selection.exit().remove();

  return mergedSelection;
};

const drawTotalArcOutline = (
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  geometry: StrokeGeometry,
  outerRadius: number,
  totalArcStroke: string
) => {
  const totalArcOutline = d3
    .arc<d3.DefaultArcObject>()
    .innerRadius(outerRadius + ARC_GEOMETRY.TOTAL_ARC_OFFSET)
    .outerRadius(outerRadius + ARC_GEOMETRY.TOTAL_ARC_OFFSET);

  const totalArcPath = totalArcOutline({
    startAngle: alphaToTheta(geometry.extents.catchAlpha),
    endAngle: alphaToTheta(geometry.extents.finishAlpha),
    innerRadius: outerRadius + ARC_GEOMETRY.TOTAL_ARC_OFFSET,
    outerRadius: outerRadius + ARC_GEOMETRY.TOTAL_ARC_OFFSET,
  } as d3.DefaultArcObject);

  group
    .append('path')
    .attr('class', 'total-arc-outline')
    .attr('d', totalArcPath || undefined)
    .attr('fill', 'none')
    .attr('stroke', totalArcStroke)
    .attr('stroke-width', 2)
    .attr('stroke-dasharray', '6 4')
    .attr('stroke-linecap', 'round')
    .attr('opacity', getArcChartChrome().totalArcOpacity);
};

const drawCircumference = (
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  outerRadius: number,
  display: DisplayConfig,
  chrome: ReturnType<typeof getArcChartChrome>
) => {
  const fullCircumference = d3
    .arc<d3.DefaultArcObject>()
    .innerRadius(outerRadius + display.circumferenceOffset)
    .outerRadius(outerRadius + display.circumferenceOffset)
    .startAngle(0)
    .endAngle(Math.PI * 2);

  const fullCircumferencePath = fullCircumference({
    startAngle: 0,
    endAngle: Math.PI * 2,
    innerRadius: outerRadius + display.circumferenceOffset,
    outerRadius: outerRadius + display.circumferenceOffset,
  } as d3.DefaultArcObject);

  group
    .append('path')
    .attr('class', 'arc-full-circumference')
    .attr('class', 'arc-full-circumference')
    .attr('d', fullCircumferencePath || undefined)
    .attr('fill', 'none')
    .attr('stroke', chrome.grid)
    .attr('stroke-width', 1.5)
    .attr('stroke-linecap', 'round');
};

const drawDegreeBelt = (
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  outerRadius: number,
  display: DisplayConfig,
  chrome: ReturnType<typeof getArcChartChrome>
) => {
  const beltGroup = group.append('g').attr('class', 'arc-degree-belt');
  const degreeTicks = d3.range(0, 181, display.degreeTickStep);

  degreeTicks.forEach((deg) => {
    const angle = (deg * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const x1 = (outerRadius + display.degreeTickInnerOffset) * cos;
    const y1 = -(outerRadius + display.degreeTickInnerOffset) * sin;
    const x2 = (outerRadius + display.degreeTickOuterOffset) * cos;
    const y2 = -(outerRadius + display.degreeTickOuterOffset) * sin;

    beltGroup
      .append('line')
      .attr('class', 'arc-degree-tick')
      .attr('x1', x1)
      .attr('y1', y1)
      .attr('x2', x2)
      .attr('y2', y2)
      .attr('stroke', chrome.axisTick)
      .attr('stroke-width', deg % (display.degreeTickStep * 2) === 0 ? 1.8 : 1.1);

    const labelX = (outerRadius + display.degreeLabelRadius) * cos;
    const labelY = -(outerRadius + display.degreeLabelRadius) * sin;

    beltGroup
      .append('text')
      .attr('class', 'arc-degree-label')
      .attr('x', labelX)
      .attr('y', labelY)
      .attr('dy', 4)
      .attr('text-anchor', Math.abs(sin) < 0.1 ? (cos > 0 ? 'start' : 'end') : 'middle')
      .attr('font-size', '10px')
      .attr('font-family', 'Inter, sans-serif')
      .attr('fill', chrome.axisLabel)
      .text(`${Math.abs(90 - deg)}°`);
  });
};

interface BoatFrameResult {
  centerlineY: number;
}

const drawBoatFrame = (
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  outerRadius: number,
  animationConfig: typeof defaultAnimationConfig,
  showReferenceLine: boolean,
  display: DisplayConfig,
  chrome: ReturnType<typeof getArcChartChrome>
): BoatFrameResult => {
  const centerlineY = outerRadius * display.centerlineRatio;

  const boatCenterline = group
    .append('line')
    .attr('class', 'arc-boat-centerline')
    .attr('x1', -outerRadius)
    .attr('y1', centerlineY)
    .attr('x2', outerRadius)
    .attr('y2', centerlineY)
    .attr('stroke', chrome.axisTick)
    .attr('stroke-width', ARC_GEOMETRY.CENTERLINE_STROKE_WIDTH)
    .attr('stroke-linecap', 'round')
    .attr('opacity', 0.85);

  if (animationConfig.enabled) {
    boatCenterline.style('opacity', 0).transition().delay(400).duration(300).style('opacity', 0.85);
  }

  if (showReferenceLine) {
    const referenceLine = group
      .append('line')
      .attr('class', 'arc-reference-line')
      .attr('x1', -outerRadius - 18)
      .attr('y1', 0)
      .attr('x2', outerRadius + 18)
      .attr('y2', 0)
      .attr('stroke', chrome.referenceLine)
      .attr('stroke-width', 1.2);

    if (animationConfig.enabled) {
      referenceLine.style('opacity', 0).transition().delay(600).duration(300).style('opacity', 1);
    }
  }

  return { centerlineY };
};

const configureLegend = (
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  dimensions: { width: number; height: number },
  legendData: LegendItem[],
  handleSegmentToggle: (segmentId: string) => void,
  activeSegment: string | null,
  animationConfig: typeof defaultAnimationConfig,
  display: DisplayConfig,
  hasAnimated: React.MutableRefObject<boolean>
) => {
  const legend = createUnifiedLegend(svg, legendData, {
    position: 'bottom',
    orientation: 'horizontal',
    itemSpacing: display.legendItemSpacing,
    interactive: true,
    showLabels: true,
    onItemClick: (item) => handleSegmentToggle(item.key),
  });

  positionLegend(legend, dimensions, { position: 'bottom', offsetY: 6 });

  const legendItems = legend.selectAll<SVGGElement, LegendItem>('.legend-item').data(legendData);

  styleLegendFocus(legendItems, activeSegment);

  // Only animate legend on first render, not on subsequent updates
  if (animationConfig.enabled && !hasAnimated.current) {
    legendItems
      .style('opacity', 0)
      .transition()
      .delay((_, i) => 900 + i * animationConfig.delay)
      .duration(300)
      .style('opacity', 1)
      .on('end', function () {
        d3.select(this).style('opacity', null);
        styleLegendFocus(legendItems, activeSegment);
      });
    hasAnimated.current = true;
  }

  return legendItems;
};

type AnimationConfig = {
  duration: number;
  delay: number;
  enabled: boolean;
  strokeDuration: number;
  driveRatio: number;
};

const defaultAnimationConfig: AnimationConfig = {
  duration: ARC_ANIMATION.duration,
  delay: ARC_ANIMATION.delay,
  enabled: ARC_ANIMATION.enabled,
  strokeDuration: ARC_ANIMATION.strokeDuration,
  driveRatio: ARC_ANIMATION.driveRatio,
};

interface RowingArcD3Props {
  sessionId?: string | null;
  strokes?: ArcStrokeData[];
  activeStrokeIndex?: number;
  playbackElapsedMsRef?: React.MutableRefObject<number>;
  strokeDurationMs?: number;
  isPlaying?: boolean;
  ghostStroke?: ArcStrokeData | null;
  targetGuides?: {
    catchMin: number;
    catchMax: number;
    finishMin: number;
    finishMax: number;
  };
  config?: {
    display?: Partial<DisplayConfig>;
    animation?: Partial<AnimationConfig>;
  };
  rowerPosition?: number | null;
  initialRowerPosition?: number;
  embedded?: boolean;
  showHeader?: boolean;
}

const useResizeObserver = (ref: React.RefObject<HTMLDivElement>) => {
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      const size = Math.min(width, height, 640);
      setDimensions({ width: size, height: size });
    });

    if (ref.current) {
      observer.observe(ref.current);
      const rect = ref.current.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height, 640);
      setDimensions({ width: size, height: size });
    }

    return () => observer.disconnect();
  }, [ref]);

  return dimensions;
};

const RowingArcD3: React.FC<RowingArcD3Props> = ({
  sessionId = null,
  strokes: providedStrokes,
  activeStrokeIndex,
  playbackElapsedMsRef,
  strokeDurationMs,
  isPlaying,
  ghostStroke = null,
  targetGuides,
  config: userConfig,
  rowerPosition,
  initialRowerPosition,
  embedded = false,
  showHeader: _showHeader = true,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(containerRef);
  const { resolved: resolvedTheme } = useTheme();

  useEffect(() => {
    applyArcThemeColors(svgRef.current);
    const arc = getArcChartChrome();
    tooltipRef.current?.style('background', arc.tooltipBg).style('color', arc.tooltipFg);
  }, [resolvedTheme]);

  const [activeSegment, setActiveSegment] = useState<string | null>(null);
  const [internalSelectedRower, setInternalSelectedRower] = useState<number | null>(
    rowerPosition ?? initialRowerPosition ?? null
  );
  const isControlledRower = rowerPosition !== undefined;
  const selectedRower = isControlledRower ? (rowerPosition ?? null) : internalSelectedRower;

  const segmentsRef = useRef<d3.Selection<
    SVGPathElement,
    SegmentDatum,
    SVGGElement,
    unknown
  > | null>(null);
  const legendItemsRef = useRef<d3.Selection<SVGGElement, LegendItem, SVGGElement, unknown> | null>(
    null
  );
  const activeSegmentRef = useRef<string | null>(activeSegment);
  activeSegmentRef.current = activeSegment;
  const segmentDataRef = useRef<SegmentDatum[]>([]);
  const initializedRef = useRef(false);
  const rootGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const overlayGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const segmentsGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const hullGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const oarGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const legendGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const tooltipRef = useRef<d3.Selection<HTMLDivElement, unknown, d3.BaseType, unknown> | null>(
    null
  );
  const bowLabelRef = useRef<d3.Selection<SVGTextElement, unknown, null, undefined> | null>(null);
  const sternLabelRef = useRef<d3.Selection<SVGTextElement, unknown, null, undefined> | null>(null);
  const oarPivotRef = useRef<d3.Selection<SVGCircleElement, unknown, null, undefined> | null>(null);
  const oarShaftRef = useRef<d3.Selection<SVGLineElement, unknown, null, undefined> | null>(null);
  const oarBladeRef = useRef<d3.Selection<SVGPathElement, unknown, null, undefined> | null>(null);
  const centerlineRef = useRef<d3.Selection<SVGLineElement, unknown, null, undefined> | null>(null);
  const referenceLineRef = useRef<d3.Selection<SVGLineElement, unknown, null, undefined> | null>(
    null
  );
  const targetMarkersGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(
    null
  );
  const gridGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const playbackSegmentRef = useRef<string | null>(null);
  const previousRenderedStrokeIndexRef = useRef<number | null>(null);
  const centerlineYRef = useRef<number>(0);
  const hasAnimatedLegendRef = useRef(false);
  const arcGeneratorRef = useRef<d3.Arc<any, SegmentDatum> | null>(null);
  const isPlayingRef = useRef(isPlaying ?? false);
  const strokeDurationRef = useRef(strokeDurationMs ?? ARC_ANIMATION.strokeDuration);

  useEffect(() => {
    isPlayingRef.current = isPlaying ?? false;
  }, [isPlaying]);

  useEffect(() => {
    strokeDurationRef.current = strokeDurationMs ?? ARC_ANIMATION.strokeDuration;
  }, [strokeDurationMs]);

  const providedStrokeSamples = useMemo<ArcStrokeData[]>(
    () => providedStrokes ?? [],
    [providedStrokes]
  );
  const isUsingProvidedStrokes = providedStrokeSamples.length > 0;

  // Use arc metrics hook only when we don't have provided sample strokes
  const arcMetricsQuery = useArcMetrics(sessionId ?? null, {
    enabled: !isUsingProvidedStrokes && !!sessionId,
  });

  const arcMetricsSamples = useMemo<ArcStrokeSample[]>(
    () => arcMetricsQuery.data ?? [],
    [arcMetricsQuery.data]
  );

  const geometryLoading = !isUsingProvidedStrokes && arcMetricsQuery.isLoading;
  const geometryError = !isUsingProvidedStrokes
    ? ((arcMetricsQuery.error as Error | null) ?? null)
    : null;

  const rowerOptions = useMemo(() => {
    const unique = new Set<number>();
    if (isUsingProvidedStrokes) {
      providedStrokeSamples.forEach((sample) => {
        if (typeof sample.rowerPosition === 'number' && Number.isFinite(sample.rowerPosition)) {
          unique.add(sample.rowerPosition);
        }
      });
    } else {
      arcMetricsSamples.forEach((sample) => {
        if (typeof sample.rowerPosition === 'number' && Number.isFinite(sample.rowerPosition)) {
          unique.add(sample.rowerPosition);
        }
      });
    }
    return Array.from(unique).sort((a, b) => a - b);
  }, [arcMetricsSamples, isUsingProvidedStrokes, providedStrokeSamples]);

  useEffect(() => {
    if (rowerOptions.length === 0) {
      if (!isControlledRower) {
        setInternalSelectedRower(null);
      }
      return;
    }
    if (!isControlledRower) {
      setInternalSelectedRower((prev) => {
        if (prev && rowerOptions.includes(prev)) {
          return prev;
        }
        return rowerOptions[0];
      });
    }
  }, [isControlledRower, rowerOptions]);

  const strokeSamples = useMemo<ArcStrokeData[]>(() => {
    if (isUsingProvidedStrokes) {
      if (selectedRower == null) {
        return providedStrokeSamples;
      }
      return providedStrokeSamples
        .filter((stroke) => stroke.rowerPosition === selectedRower)
        .sort((a, b) => a.strokeIndex - b.strokeIndex);
    }

    if (!sessionId || !selectedRower) {
      return [];
    }

    return arcMetricsSamples
      .filter((arcSample) => arcSample.rowerPosition === selectedRower)
      .map((arcSample) => mapArcSampleToStrokeSample(arcSample, sessionId ?? 'sample-session'));
  }, [arcMetricsSamples, isUsingProvidedStrokes, providedStrokeSamples, selectedRower, sessionId]);

  const playbackStrokeIndex = activeStrokeIndex ?? 0;

  const effectiveStrokeIndex = useMemo(() => {
    if (!strokeSamples.length) {
      return 0;
    }
    return Math.min(strokeSamples.length - 1, Math.max(0, playbackStrokeIndex));
  }, [strokeSamples, playbackStrokeIndex]);

  const activeStroke = strokeSamples[effectiveStrokeIndex] ?? null;

  const displayConfig = useMemo(
    () => ({
      ...defaultDisplayConfig,
      ...userConfig?.display,
    }),
    [userConfig?.display]
  );

  const animationConfig = useMemo(
    () => ({
      ...defaultAnimationConfig,
      ...userConfig?.animation,
    }),
    [userConfig?.animation]
  );

  const geometry = useMemo(
    () => (activeStroke ? calculateStrokeGeometry(activeStroke) : null),
    [activeStroke]
  );

  const ghostGeometry = useMemo(
    () => (ghostStroke ? calculateStrokeGeometry(ghostStroke) : null),
    [ghostStroke]
  );

  const dataSourceNote = useMemo(() => {
    if (!activeStroke) return null;
    if (activeStroke.dataSource === 'peach_0x800A') {
      return 'Measured (Peach 0x800A)';
    }
    if (activeStroke.dataSource === 'manual_override') {
      return 'Manual Override';
    }
    return null;
  }, [activeStroke]);

  const chartCalculations = useMemo(() => {
    const { width, height } = dimensions;
    const centerX = width / 2;
    const plotHeight = Math.max(height - ARC_LEGEND_RESERVE, height * 0.82);
    const centerY = plotHeight * 0.46;
    const baseRadius = Math.min(width, plotHeight) * 0.35;
    const outerRadius = Math.min(baseRadius, displayConfig.outerRadius);
    const innerRadius = displayConfig.innerRadius;

    return { centerX, centerY, outerRadius, innerRadius };
  }, [dimensions, displayConfig.innerRadius, displayConfig.outerRadius]);

  const handleSegmentToggle = useCallback((segmentId: string) => {
    setActiveSegment((prev) => (prev === segmentId ? null : segmentId));
  }, []);

  const applySegmentFocus = useCallback((focusId: string | null) => {
    const selection = segmentsRef.current;
    if (!selection) return;
    const arc = getArcChartChrome();
    selection
      .attr('opacity', (d) => (!focusId || focusId === d.id ? 1 : arc.inactiveSegmentOpacity))
      .attr('stroke-width', (d) =>
        focusId && focusId === d.id ? arc.segmentActiveStrokeWidth : arc.segmentStrokeWidth
      )
      .attr('aria-pressed', (d) => (focusId ? focusId === d.id : false));
  }, []);

  const applyLegendFocus = useCallback((focusId: string | null) => {
    const legendItems = legendItemsRef.current;
    if (!legendItems) return;
    styleLegendFocus(legendItems, focusId);
  }, []);

  /**
   * PHASE 1: Initialize static SVG structure
   *
   * This effect sets up the permanent SVG structure that doesn't change
   * during playback:
   * - Centerline (bow/stern)
   * - Bow/Stern labels
   * - Degree belt and ticks
   * - Reference lines
   * - Element groups for dynamic content
   *
   * Dependencies: dimensions, displayConfig, animationConfig
   * Does NOT depend on: geometry (that would cause rebuilds on every stroke)
   */
  useEffect(() => {
    const svgElement = svgRef.current ? d3.select(svgRef.current) : null;

    if (!svgElement) {
      return;
    }

    // Skip re-initialization if already done for these dimensions
    if (initializedRef.current) {
      return;
    }

    svgElement.selectAll('*').remove();

    const tooltip = createTooltip();
    tooltipRef.current = tooltip;

    const { centerX, centerY, innerRadius, outerRadius } = chartCalculations;

    const rootGroup = svgElement
      .append('g')
      .attr('class', 'arc-root')
      .attr('transform', `translate(${centerX}, ${centerY})`);
    rootGroupRef.current = rootGroup;

    const gridGroup = rootGroup.append('g').attr('class', 'grid-lines');
    const targetMarkersGroup = rootGroup.append('g').attr('class', 'arc-target-ranges');
    const overlayGroup = rootGroup.append('g').attr('class', 'arc-ghost-overlay');
    const segmentsGroup = rootGroup.append('g').attr('class', 'segments');
    const hullGroup = rootGroup.append('g').attr('class', 'hull');
    const ticksGroup = rootGroup.append('g').attr('class', 'degree-layer');
    const oarGroup = rootGroup.append('g').attr('class', 'arc-oar-group');

    overlayGroupRef.current = overlayGroup;
    segmentsGroupRef.current = segmentsGroup;
    hullGroupRef.current = hullGroup;
    oarGroupRef.current = oarGroup;
    targetMarkersGroupRef.current = targetMarkersGroup;
    gridGroupRef.current = gridGroup;

    // Grid lines will be drawn in the update effect when geometry is available

    const arcChrome = getArcChartChrome();
    drawCircumference(ticksGroup, outerRadius, displayConfig, arcChrome);
    drawDegreeBelt(ticksGroup, outerRadius, displayConfig, arcChrome);

    const { centerlineY } = drawBoatFrame(
      hullGroup,
      outerRadius,
      animationConfig,
      displayConfig.showReferenceLines,
      displayConfig,
      arcChrome
    );
    centerlineYRef.current = centerlineY;
    centerlineRef.current = hullGroup.select<SVGLineElement>('line.arc-boat-centerline');
    referenceLineRef.current = hullGroup.select<SVGLineElement>('line.arc-reference-line');

    const labelInset = 14;
    const labelOffsetY = 18;

    const bowLabel = hullGroup
      .append('text')
      .attr('class', 'arc-bow-label')
      .attr('x', outerRadius + labelInset)
      .attr('y', centerlineY + labelOffsetY)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'hanging')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-size', '14px')
      .attr('font-weight', 600)
      .attr('fill', arcChrome.axisLabel)
      .text('Bow');

    const sternLabel = hullGroup
      .append('text')
      .attr('class', 'arc-stern-label')
      .attr('x', -(outerRadius + labelInset))
      .attr('y', centerlineY + labelOffsetY)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'hanging')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-size', '14px')
      .attr('font-weight', 600)
      .attr('fill', arcChrome.axisLabel)
      .text('Stern');

    bowLabelRef.current = bowLabel;
    sternLabelRef.current = sternLabel;
    bowLabel.raise();
    sternLabel.raise();

    const oarPivot = oarGroup
      .append('circle')
      .attr('class', 'arc-oar-pivot')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', ARC_GEOMETRY.OAR_PIVOT_RADIUS)
      .attr('fill', arcChrome.oarPivot)
      .attr('opacity', 0.85);

    const oarShaft = oarGroup
      .append('line')
      .attr('class', 'arc-oar-shaft')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 0)
      .attr('stroke', arcChrome.oarShaft)
      .attr('stroke-width', ARC_GEOMETRY.OAR_SHAFT_WIDTH)
      .attr('stroke-linecap', 'round');

    const oarBlade = oarGroup
      .append('path')
      .attr('class', 'arc-oar-blade')
      .attr('fill', arcChrome.oarShaft)
      .attr('stroke', arcChrome.oarShaft)
      .attr('stroke-width', 1.2)
      .attr('opacity', 0.95);

    oarGroup.raise();

    oarPivotRef.current = oarPivot;
    oarShaftRef.current = oarShaft;
    oarBladeRef.current = oarBlade;

    // Create arc generator and store in ref for updates
    const arcGenerator = d3
      .arc<SegmentDatum>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .startAngle((d) => alphaToTheta(d.startAlpha))
      .endAngle((d) => alphaToTheta(d.endAlpha));
    arcGeneratorRef.current = arcGenerator;

    // Create legend with placeholder data (will be populated when geometry available)
    const placeholderLegendData: LegendItem[] = [
      { key: 'catch-slip', position: 1, label: 'Catch Gap', color: arcChrome.catchSlip },
      {
        key: 'effective-length',
        position: 2,
        label: 'Effective Length',
        color: arcChrome.effectiveLength,
      },
      { key: 'finish-slip', position: 3, label: 'Finish Slip', color: arcChrome.finishSlip },
    ];

    const legendItems = configureLegend(
      svgElement,
      dimensions,
      placeholderLegendData,
      handleSegmentToggle,
      activeSegmentRef.current,
      animationConfig,
      displayConfig,
      hasAnimatedLegendRef
    );
    legendItemsRef.current = legendItems;

    const legendParent = legendItems.empty()
      ? null
      : d3.select<SVGGElement, unknown>(legendItems.node()?.parentNode as SVGGElement);
    legendGroupRef.current = legendParent;

    initializedRef.current = true;

    return () => {
      initializedRef.current = false;
      hasAnimatedLegendRef.current = false;
      tooltipRef.current?.remove();
      tooltipRef.current = null;
      segmentsRef.current = null;
      legendItemsRef.current = null;
      rootGroupRef.current = null;
      overlayGroupRef.current = null;
      segmentsGroupRef.current = null;
      hullGroupRef.current = null;
      oarGroupRef.current = null;
      legendGroupRef.current = null;
      bowLabelRef.current = null;
      sternLabelRef.current = null;
      oarPivotRef.current = null;
      oarShaftRef.current = null;
      oarBladeRef.current = null;
      centerlineRef.current = null;
      referenceLineRef.current = null;
      targetMarkersGroupRef.current = null;
      gridGroupRef.current = null;
      playbackSegmentRef.current = null;
      arcGeneratorRef.current = null;
      svgElement.selectAll('*').remove();
    };
  }, [animationConfig, chartCalculations, displayConfig, dimensions, handleSegmentToggle]);

  /**
   * PHASE 2: Update dynamic elements when geometry changes
   *
   * This effect updates the arc segments and total arc outline when a new
   * stroke is selected. It does NOT rebuild the entire SVG structure.
   *
   * Dependencies: geometry, activeSegment
   * Uses refs to: arcGenerator, segments, legend, hull group
   */
  useEffect(() => {
    if (
      !initializedRef.current ||
      !geometry ||
      !segmentsGroupRef.current ||
      !hullGroupRef.current
    ) {
      return;
    }

    const tooltip =
      tooltipRef.current ??
      (() => {
        const newTooltip = createTooltip();
        tooltipRef.current = newTooltip;
        return newTooltip;
      })();

    const arcGenerator = arcGeneratorRef.current;
    if (!arcGenerator) {
      return;
    }

    const { innerRadius, outerRadius } = chartCalculations;

    // Build segment data for current geometry
    const segmentData = buildSegmentData(geometry);
    segmentDataRef.current = segmentData;

    if (overlayGroupRef.current) {
      if (ghostGeometry) {
        const ghostSegmentData = buildSegmentData(ghostGeometry);
        overlayGroupRef.current
          .selectAll<SVGPathElement, SegmentDatum>('.arc-ghost-segment')
          .data(ghostSegmentData, (d) => d.id)
          .join(
            (enter) =>
              enter
                .append('path')
                .attr('class', (d) => `arc-ghost-segment arc-ghost-segment--${d.id}`),
            (update) => update,
            (exit) => exit.remove()
          )
          .attr('d', (d) => arcGenerator(d) || undefined);
      } else {
        overlayGroupRef.current.selectAll('.arc-ghost-segment').remove();
      }
    }

    // Update total arc outline (depends on geometry extents)
    // Remove old outline if exists
    hullGroupRef.current.selectAll('.total-arc-outline').remove();
    drawTotalArcOutline(hullGroupRef.current, geometry, outerRadius, getArcChartChrome().totalArc);

    // Update grid lines if showGrid is enabled (depends on geometry extents)
    if (displayConfig.showGrid && gridGroupRef.current) {
      gridGroupRef.current.selectAll('line').remove();
      [geometry.extents.catchAlpha, 0, geometry.extents.finishAlpha].forEach((alpha) => {
        const angle = alphaToTheta(alpha);
        gridGroupRef
          .current!.append('line')
          .attr('x1', Math.cos(angle) * innerRadius)
          .attr('y1', Math.sin(angle) * innerRadius)
          .attr('x2', Math.cos(angle) * outerRadius)
          .attr('y2', Math.sin(angle) * outerRadius)
          .attr('stroke', 'rgba(148, 163, 184, 0.25)')
          .attr('stroke-width', 1);
      });
    }

    // Update or create arc segments using D3 join pattern
    // Pass false for isInitialRender to skip entrance animations on geometry updates
    const shouldAnimateStrokeChange =
      previousRenderedStrokeIndexRef.current != null &&
      previousRenderedStrokeIndexRef.current !== effectiveStrokeIndex;

    const segmentSelection = renderSegments(
      segmentsGroupRef.current,
      segmentData,
      arcGenerator,
      tooltip,
      animationConfig,
      handleSegmentToggle,
      activeSegmentRef,
      false, // Don't animate on geometry updates
      shouldAnimateStrokeChange
    );
    segmentsRef.current = segmentSelection;
    previousRenderedStrokeIndexRef.current = effectiveStrokeIndex;

    const focusId = activeSegmentRef.current ?? playbackSegmentRef.current ?? null;
    applySegmentFocus(focusId);
    applyLegendFocus(focusId);

    if (targetMarkersGroupRef.current) {
      const markers = targetMarkersGroupRef.current;
      const targetGuidesData = [
        {
          id: 'catch-guide',
          type: 'catch',
          startAlpha: -(targetGuides?.catchMax ?? 58),
          endAlpha: -(targetGuides?.catchMin ?? 56),
        },
        {
          id: 'finish-guide',
          type: 'finish',
          startAlpha: targetGuides?.finishMin ?? 30,
          endAlpha: targetGuides?.finishMax ?? 34,
        },
      ];
      const guideRadius = outerRadius + displayConfig.circumferenceOffset + 7;

      const guideArcPath = (startAlpha: number, endAlpha: number) => {
        const path = d3.path();
        path.arc(0, 0, guideRadius, alphaToTheta(startAlpha), alphaToTheta(endAlpha), false);
        return path.toString();
      };

      markers
        .selectAll<SVGPathElement, (typeof targetGuidesData)[number]>('path')
        .data(targetGuidesData, (d) => d.id)
        .join(
          (enter) =>
            enter
              .append('path')
              .attr('class', (d) => `arc-target-guide arc-target-guide--${d.type}`),
          (update) => update,
          (exit) => exit.remove()
        )
        .attr('d', (d) => guideArcPath(d.startAlpha, d.endAlpha));
    }
  }, [
    activeSegment,
    animationConfig,
    applyLegendFocus,
    applySegmentFocus,
    chartCalculations,
    displayConfig,
    effectiveStrokeIndex,
    geometry,
    ghostGeometry,
    handleSegmentToggle,
    targetGuides,
    resolvedTheme,
  ]);

  const updateOarForProgress = useCallback(
    (progressValueRaw: number) => {
      if (!geometry) {
        return;
      }

      const oarShaft = oarShaftRef.current;
      const oarBlade = oarBladeRef.current;
      if (!oarShaft || !oarBlade) {
        return;
      }

      const { outerRadius } = chartCalculations;
      const oarRadius = outerRadius + displayConfig.oarRadiusOffset;
      const handleLength = centerlineYRef.current + displayConfig.handleExtraLength;
      const bladeLength = displayConfig.bladeLength;
      const bladeWidth = displayConfig.bladeWidth;
      const driveRatio = Math.max(
        0.2,
        Math.min(animationConfig.driveRatio ?? ARC_PHYSICS.OAR_DRIVE_RATIO, 0.8)
      );
      const catchAlpha = geometry.extents.catchAlpha;
      const finishAlpha = geometry.extents.finishAlpha;
      const alphaRange = finishAlpha - catchAlpha;

      const computeAlphaForProgress = (progress: number) => {
        const clamped = Math.max(0, Math.min(1, progress));
        if (clamped < driveRatio) {
          const local = driveRatio > 0 ? clamped / driveRatio : 0;
          return catchAlpha + (alphaRange / 2) * (1 - Math.cos(Math.PI * local));
        }
        const recovery = Math.max(1 - driveRatio, 1e-4);
        const local = (clamped - driveRatio) / recovery;
        return finishAlpha - (alphaRange / 2) * (1 - Math.cos(Math.PI * local));
      };

      const progressValue = Number.isFinite(progressValueRaw) ? progressValueRaw : 0;
      const alpha = computeAlphaForProgress(progressValue);

      const theta = alphaToTheta(alpha);
      const unitX = Math.sin(theta);
      const unitY = -Math.cos(theta);

      const bladeRootX = unitX * oarRadius;
      const bladeRootY = unitY * oarRadius;
      const bladeTipX = unitX * (oarRadius + bladeLength);
      const bladeTipY = unitY * (oarRadius + bladeLength);
      const handleX = unitX * -handleLength;
      const handleY = unitY * -handleLength;

      oarShaft
        .attr('x1', handleX)
        .attr('y1', handleY)
        .attr('x2', bladeRootX)
        .attr('y2', bladeRootY);

      const perpX = -unitY;
      const perpY = unitX;

      const bladePath = buildBladePath(
        bladeRootX,
        bladeRootY,
        bladeTipX,
        bladeTipY,
        perpX,
        perpY,
        bladeWidth
      );
      oarBlade.attr('d', bladePath);

      const segmentData = segmentDataRef.current;
      let playbackSegment: string | null = null;
      if (segmentData.length > 0) {
        const match = segmentData.find(
          (segment) => alpha >= segment.startAlpha && alpha <= segment.endAlpha
        );
        playbackSegment = match ? match.id : null;
      }
      playbackSegmentRef.current = playbackSegment;

      const focusId = activeSegmentRef.current ?? playbackSegment;
      applySegmentFocus(focusId);
      applyLegendFocus(focusId);
    },
    [
      animationConfig,
      applyLegendFocus,
      applySegmentFocus,
      chartCalculations,
      displayConfig,
      geometry,
    ]
  );

  useEffect(() => {
    if (!geometry) {
      return;
    }

    let frameId: number | null = null;

    const renderFrame = () => {
      const elapsed = playbackElapsedMsRef?.current ?? 0;
      const durationMs = Math.max(1, strokeDurationRef.current);
      const progressValue = (((elapsed % durationMs) + durationMs) % durationMs) / durationMs;
      updateOarForProgress(progressValue);

      if (isPlayingRef.current) {
        frameId = requestAnimationFrame(renderFrame);
      }
    };

    renderFrame();

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [geometry, playbackElapsedMsRef, updateOarForProgress]);

  useEffect(() => {
    activeSegmentRef.current = activeSegment;
    const focusId = activeSegment ?? playbackSegmentRef.current ?? null;
    applySegmentFocus(focusId);
    applyLegendFocus(focusId);
  }, [activeSegment, applyLegendFocus, applySegmentFocus]);

  useEffect(() => {
    setActiveSegment(null);
  }, [effectiveStrokeIndex, selectedRower, sessionId]);

  const renderGeometryContent = () => {
    if (geometryLoading) {
      return <p className="arc-prototype-info">Loading stroke geometry…</p>;
    }

    if (geometryError) {
      return (
        <p className="arc-prototype-error" role="alert">
          Failed to load stroke geometry: {geometryError.message || 'Unknown error'}
        </p>
      );
    }

    if (!activeStroke || !geometry) {
      return <p className="arc-prototype-info">No stroke geometry available for this selection.</p>;
    }

    return (
      <>
        <div className="arc-prototype-chart-wrapper" ref={containerRef}>
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}
          />
        </div>
      </>
    );
  };

  const innerContent = (
    <>
      {!embedded && (
        <div className="arc-prototype-toolbar">
          <label>
            Seat
            <select
              value={selectedRower ?? ''}
              onChange={(event) =>
                setInternalSelectedRower(event.target.value ? Number(event.target.value) : null)
              }
              disabled={rowerOptions.length === 0}
            >
              {rowerOptions.length === 0 && (
                <option value="" disabled>
                  No seats detected
                </option>
              )}
              {rowerOptions.map((rower) => (
                <option key={rower} value={rower}>
                  Seat {rower}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setActiveSegment(null)}
            className="arc-prototype-reset"
            disabled={!activeSegment}
          >
            Reset Highlights
          </button>
        </div>
      )}

      {dataSourceNote && <p className="arc-prototype-info">{dataSourceNote}</p>}

      {renderGeometryContent()}
    </>
  );

  if (embedded) {
    return innerContent;
  }

  return (
    <div className="arc-prototype-root">
      <div className="arc-prototype-card">{innerContent}</div>
    </div>
  );
};

export default React.memo(RowingArcD3);

/**
 * Map ArcStrokeSample (from useArcMetrics) to ArcStrokeData (expected by RowingArcD3)
 *
 * ArcStrokeSample contains metrics calculated from telemetry_data via profile RPC.
 * ArcStrokeData is the interface used by the D3 visualization.
 */
function mapArcSampleToStrokeSample(arcSample: ArcStrokeSample, sessionId: string): ArcStrokeData {
  const { metrics, rowerPosition, strokeNumber } = arcSample;

  return {
    strokeId: `${sessionId}-${rowerPosition}-${strokeNumber}`,
    label: strokeNumber === 0 ? 'Median Profile' : `Stroke ${strokeNumber}`,
    catchAngleDeg: Math.abs(metrics.catchAngle), // Convert from negative to positive
    finishAngleDeg: metrics.finishAngle,
    catchSlipApplicable: metrics.catchSlipApplicable,
    catchSlipConfidence: metrics.catchSlipConfidence,
    catchSlipDeg: metrics.catchSlipAngle,
    finishSlipDeg: metrics.finishSlipAngle,
    driveStartMs: null, // Not available from profile data
    driveTimeMs: null, // Not available from profile data
    recoveryTimeMs: null, // Not available from profile data
    dataSource: 'telemetry_profile', // Calculated from telemetry_data via profile RPC
    confidence: null, // Profile is median across strokes, no single confidence score
    strokeIndex: strokeNumber,
    rowerPosition,
  };
}
